// IMPORTANT!!!: Ensure this is up to date with firebaseConfig.tsx in functions/src folder

import {
    FirebaseApp,
    initializeApp,
    getApps,
}                               from 'firebase/app';
import {
    getAnalytics,
}                               from 'firebase/analytics';
import {
    getFirestore,
    initializeFirestore,
}                               from 'firebase/firestore';
import {
    initializeAuth,
    getAuth,
    browserSessionPersistence,
    browserLocalPersistence,
    indexedDBLocalPersistence,
    browserPopupRedirectResolver,
    Auth,
    UserCredential,
    getRedirectResult,
}                               from 'firebase/auth';

// ===== Services =====

// Do not import from './services' (the barrel) in this file.
// `fire` is a low-level dependency used by many services; importing the barrel
// can re-import modules that depend on `fire`, causing a circular init path
// and runtime TDZ errors like "Cannot access 'bundle' before initialization".
import {
    logAuthDebug,
    shouldEnableAuthRuntimeConfigLog,
}                               from './services/authDebug';

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
// Is it safe to expose this in build? Yes (https://stackoverflow.com/questions/37482366/is-it-safe-to-expose-firebase-apikey-to-the-public)
const runtimeAuthDomain = (() => {
    if (typeof window !== 'undefined') {
        const { host, protocol, hostname } = window.location;
        const isHttps = protocol === 'https:';
        const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
        // Keep redirect auth same-site on preview/custom domains (critical for iOS Safari).
        if (isHttps && !isLocalhost) {
            return host;
        }
    }
    return process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;
})();

const config = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: runtimeAuthDomain,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const shouldShowAuthRuntimeConfigLog = shouldEnableAuthRuntimeConfigLog({
    search: typeof window !== 'undefined' ? window.location.search : '',
    envValue: process.env.REACT_APP_ENABLE_AUTH_RUNTIME_CONFIG_LOGS,
    isDevelopment: process.env.NODE_ENV === 'development',
});

if (shouldShowAuthRuntimeConfigLog && typeof window !== 'undefined') {
    // Helps verify runtime authDomain selection during redirect-auth debugging.
    // eslint-disable-next-line no-console
    console.info('[auth] runtime config', {
        authDomain: runtimeAuthDomain,
        origin: window.location.origin,
    });
}

type FireBundle = {
    app: FirebaseApp,
    auth: Auth,
    redirectReady: Promise<UserCredential | null> | null,
    hadPendingRedirect: boolean,
};
const globalKey = '__VERASCOPE_FIRE__';

function build(): FireBundle {
    const existingApps = getApps();
    const [existingApp] = existingApps;
    const app = existingApp ?? initializeApp(config);
    const shouldForceLongPolling = typeof window !== 'undefined'
        && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    try {
        initializeFirestore(
            app,
            shouldForceLongPolling
                ? {
                    experimentalForceLongPolling: true,
                }
                : {
                    experimentalAutoDetectLongPolling: true,
                },
        );
    } catch {
        getFirestore(app);
    }

    // Analytics can throw outside browser / if consent not granted.
    // eslint-disable-next-line no-empty
    try { if (config.measurementId) getAnalytics(app); } catch {}

    // Safari / iOS detection
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const isSafari = /\bSafari\b/i.test(ua) && !/\bChrome\b/i.test(ua);
    const isIOS = /\b(iPhone|iPad|iPod)\b/i.test(ua);
    const safariLike = isSafari || isIOS;
    logAuthDebug('fire-runtime-config', {
        authDomain: runtimeAuthDomain || null,
        projectId: config.projectId || null,
        origin: typeof window !== 'undefined' ? window.location.origin : null,
        firestoreForceLongPolling: shouldForceLongPolling,
    });
    logAuthDebug('fire-auth-init-started', {
        isSafari,
        isIOS,
        safariLike,
    });

    let auth: Auth;
    if (typeof window !== 'undefined') {
        try {
            auth = initializeAuth(app, {
                persistence: safariLike
                    ? [browserSessionPersistence] // avoid IDB/localStorage reliance on Safari/I TP
                    : [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
                popupRedirectResolver: browserPopupRedirectResolver, // ok for both popup & redirect
            });
            logAuthDebug('fire-auth-initializeAuth-succeeded', {
                safariLike,
            });
        } catch {
            auth = getAuth(app); // already initialized
            logAuthDebug('fire-auth-initializeAuth-fell-back-to-getAuth', {
                safariLike,
            });
        }
    } else {
        auth = getAuth(app);
    }

    const hasStorageKeyWithPrefix = (storage: Storage | undefined, prefix: string): boolean => {
        if (!storage) return false;
        try {
            for (let i = 0; i < storage.length; i += 1) {
                const key = storage.key(i);
                if (key && key.startsWith(prefix)) return true;
            }
        } catch {
            return false;
        }
        return false;
    };

    // 🧭 Detect pending redirect marker *before* consuming it
    let hadPendingRedirect = false;
    // One shared redirect promise per page load
    let redirectReady: Promise<UserCredential | null> | null = null;

    if (typeof window !== 'undefined') {
        const w = window as any;
        // eslint-disable-next-line no-underscore-dangle
        if (!w.__redirectReady) {
            let customFlag = false;
            try {
                customFlag = sessionStorage.getItem('vs_redirecting') === '1'
                    || localStorage.getItem('vs_redirecting') === '1';
            } catch {
                customFlag = false;
            }

            const firebasePendingRedirect = hasStorageKeyWithPrefix(window.sessionStorage, 'firebase:pendingRedirect:')
                || hasStorageKeyWithPrefix(window.localStorage, 'firebase:pendingRedirect:');
            hadPendingRedirect = customFlag || firebasePendingRedirect;
            logAuthDebug('fire-redirect-pending-detected', {
                customFlag,
                firebasePendingRedirect,
                hadPendingRedirect,
            });

            // eslint-disable-next-line no-underscore-dangle
            w.__redirectReady = (async () => {
                const maxAttempts = hadPendingRedirect ? 6 : 1;
                const retryDelayMs = 400;
                let lastResult: UserCredential | null = null;
                logAuthDebug('fire-redirect-ready-started', {
                    hadPendingRedirect,
                    maxAttempts,
                    retryDelayMs,
                });

                try {
                    if (typeof (auth as any).authStateReady === 'function') {
                        await (auth as any).authStateReady();
                    }
                } catch {
                    // ignore and continue; getRedirectResult can still succeed
                }

                // eslint-disable-next-line no-await-in-loop
                for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                    try {
                        // eslint-disable-next-line no-await-in-loop
                        const res = await getRedirectResult(auth);
                        if (res) {
                            lastResult = res;
                            logAuthDebug('fire-redirect-result-resolved', {
                                attempt,
                                providerId: res.providerId || null,
                                userId: res.user?.uid || null,
                            });
                            break;
                        }
                        lastResult = null;
                        logAuthDebug('fire-redirect-result-empty', {
                            attempt,
                        });
                        if (attempt < maxAttempts) {
                            // eslint-disable-next-line no-await-in-loop
                            await new Promise((resolve) => {
                                window.setTimeout(resolve, retryDelayMs);
                            });
                        }
                    } catch (err: any) {
                        // eslint-disable-next-line no-underscore-dangle
                        w.__redirectError = err;
                        logAuthDebug('fire-redirect-result-failed', {
                            code: err?.code,
                            message: err?.message || String(err),
                        });
                        return null;
                    }
                }
                // eslint-disable-next-line no-underscore-dangle
                w.__redirectResult = lastResult ?? null;
                logAuthDebug('fire-redirect-ready-completed', {
                    hasResult: !!lastResult,
                    providerId: lastResult?.providerId || null,
                    userId: lastResult?.user?.uid || null,
                });
                return lastResult ?? null;
            })();
        }
        // eslint-disable-next-line no-underscore-dangle
        redirectReady = w.__redirectReady as Promise<UserCredential | null>;
    }

    return {
        app,
        auth,
        redirectReady,
        hadPendingRedirect,
    };
}

const bundle: FireBundle = (() => {
    const w = typeof window !== 'undefined' ? (window as any) : {};
    // eslint-disable-next-line no-return-assign
    return (w[globalKey] ??= build());
})();

export default function fire(): FireBundle {
    return bundle;
}
